import { GenericObject } from 'app/constants/GlobalTypes'
import useFilterParams from 'app/lib/hooks/useFilterParams'
import { useMemo, useState } from 'react'

type PaginatorType = {
  initalPageSize?: number
}

/**
 * Paginator hook that manages pagination state through URL search parameters
 * @param initalPageSize - Initial page size (default: 10)
 * @returns Object with pagination config and page change handler
 *
 * @example
 * ```typescript
 * // Basic usage with URL-based pagination
 * const { paginationConfig, onPageChange } = useParamsPaginator({ initalPageSize: 20 })
 *
 * // Access pagination state
 * const { pageSize, currentPage } = paginationConfig
 *
 * // Handle page changes (updates URL params)
 * const handlePageChange = (page: number, size: number) => {
 *   onPageChange(page, size)
 * }
 *
 * return (
 *   <div>
 *     <div>Current Page: {currentPage}</div>
 *     <div>Page Size: {pageSize}</div>
 *     <Pagination
 *       current={currentPage}
 *       pageSize={pageSize}
 *       total={totalItems}
 *       onChange={handlePageChange}
 *     />
 *   </div>
 * )
 * ```
 */
export const useParamsPaginator = ({ initalPageSize = 10 }: PaginatorType) => {
  const { setSearchParams, params } = useFilterParams<GenericObject<string | number | boolean>>()

  const paginationConfig = useMemo(() => {
    const pageSize = params['last'] ? Number(params['last']) : initalPageSize
    const currentPage = params['first'] ? Number(params['first']) / pageSize : 1

    return { pageSize, currentPage }
  }, [params])

  const onPageChange = (p: number, pSize = 10) => {
    setSearchParams({ ...params, first: p * pSize, last: pSize })
  }

  return { paginationConfig, onPageChange }
}

/**
 * Default paginator hook that manages pagination state in component memory
 * @param initalPageSize - Initial page size (default: 10)
 * @returns Object with pagination config and page change handler
 *
 * @example
 * ```typescript
 * // Basic usage with in-memory pagination
 * const { paginationConfig, onPageChange } = useDefaultPaginator({ initalPageSize: 15 })
 *
 * // Access pagination state
 * const { pageSize, currentPage } = paginationConfig
 *
 * // Handle page changes (updates component state)
 * const handlePageChange = (page: number, size: number) => {
 *   onPageChange(page, size)
 * }
 * ```
 */
export const useDefaultPaginator = ({ initalPageSize = 10 }: PaginatorType) => {
  const [pageSize, setPageSize] = useState<number>(initalPageSize)
  const [currentPage, setCurrentPage] = useState<number>(1)

  const paginationConfig = { pageSize, currentPage }

  const onPageChange = (p: number, pSize = 10) => {
    setCurrentPage(p)
    setPageSize(pSize)
  }

  return { paginationConfig, onPageChange }
}
